import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { useDeliveryRegionStore } from '../store/deliveryRegionStore';
import { isLocalOnlyCategory } from '../lib/brandMap';
import { useNaturesStore } from '../store/naturesStore';
import { useShippingInfo } from '../hooks/useShippingInfo';
import { formatPrice } from '../lib/utils';
import { Link } from 'react-router-dom';
// @module:cart-sidebar-imports — 選用組件（如運費試算）的 import 由 compose 注入於此行之下
import ShippingQuoteWidget from '../components/ShippingQuoteWidget';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartSidebar({ isOpen, onClose }: CartSidebarProps) {
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const subtotal = useCartStore((s) => s.subtotal());
  const discount = useCartStore((s) => s.discount());
  const finalTotal = useCartStore((s) => s.finalTotal());
  // 地址為王（客人 2026-09-05）：海外收貨時提示車內「僅本地可售」商品（結帳會擋）
  const deliveryRegion = useDeliveryRegionStore((s) => s.deliveryRegion);
  useNaturesStore((s) => s.loaded); // shipping-zones v2 定性映射載入後 badge 重算
  const localOnlyInCart = deliveryRegion === 'OS' ? items.filter((i) => isLocalOnlyCategory(i.category)) : [];
  const { label: shippingLabel } = useShippingInfo(finalTotal);
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShoppingBag className="text-x1493-primary" />
            購物車
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag size={48} className="mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500 mb-4">購物車是空的</p>
              <Link
                to="/store"
                onClick={onClose}
                className="inline-block bg-x1493-primary text-white px-6 py-2 rounded-xl font-bold"
              >
                去逛逛
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.productId} className="flex gap-3 bg-stone-50 p-3 rounded-xl">
                  <div className="w-20 h-20 rounded-lg bg-stone-200 flex-shrink-0 overflow-hidden">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🍜</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{item.name}</p>
                    <p className="text-x1493-primary font-semibold text-sm">{formatPrice(item.unitPrice)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                        className="w-7 h-7 rounded-lg bg-white border flex items-center justify-center hover:bg-stone-100"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                        className="w-7 h-7 rounded-lg bg-white border flex items-center justify-center hover:bg-stone-100"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col justify-between items-end">
                    <button
                      onClick={() => removeItem(item.productId)}
                      className="text-stone-400 hover:text-red-500 p-1"
                    >
                      <Trash2 size={18} />
                    </button>
                    <span className="font-bold text-sm">{formatPrice(item.subtotal)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-4 space-y-3 bg-stone-50">
            {
              // @module:shipping-quote — 選用組件 shipping-zones 的運費試算 widget 由 compose 注入於此行之下（空容器＝未選配零渲染）
            <ShippingQuoteWidget mode="cart" />
            }
            {localOnlyInCart.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 p-3 rounded-xl text-xs">
                <p className="font-bold mb-1">🇭🇰 以下商品只限香港本地配送，寄海外請先移除：</p>
                <p>{localOnlyInCart.map((i) => `${i.name} ×${i.quantity}`).join('、')}</p>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">小計</span>
              <span className="font-semibold">{formatPrice(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">優惠</span>
                <span className="font-semibold text-green-600">-{formatPrice(discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">運費</span>
              <span className="font-semibold">{shippingLabel}</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span>合計</span>
              <span className="text-x1493-primary">{formatPrice(finalTotal)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={onClose}
              className="block w-full text-center bg-x1493-primary text-white py-3 rounded-xl font-bold hover:bg-red-700 transition"
            >
              去結帳
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
